"use client";

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const contactInfo = [
  {
    icon: 'EnvelopeIcon',
    title: '電子郵件',
    lines: [{ text: 'info@xinyuee.com', href: null }, { text: 'wholesale@xinyuee.com', href: null }],
  },
  {
    icon: 'ClockIcon',
    title: '工作時間',
    lines: [{ text: '週一至週五 8:30–18:00', href: null }, { text: '週六 9:00–17:00', href: null }],
  },
  {
    icon: 'MapPinIcon',
    title: '公司地址',
    lines: [
      { text: '香港新界葵涌貴盛工業大廈一期', href: null },
      { text: 'Kwai Shing Industrial Building Phase 1', href: null },
    ],
  },
];

export default function ContactContent() {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    setSubmitted(true);
  };

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-container mx-auto">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-5">
            <div>
              <h2 className="font-heading font-bold text-brand-fg text-2xl mb-2">聯繫方式</h2>
              <p className="text-brand-muted text-sm leading-relaxed">
                多種聯繫方式，選擇最方便的方式與我們溝通。
              </p>
            </div>

            {contactInfo.map((info) => (
              <div
                key={info.title}
                className="flex items-start gap-4 p-5 rounded-2xl bg-secondary/40 border border-brand-border"
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #8B5E3C, #C4915A)' }}
                >
                  <Icon name={info.icon as any} size={18} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-brand-fg text-sm mb-1">{info.title}</p>
                  {info.lines.map((line) => (
                    line.href ? (
                      <a key={line.text} href={line.href} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">{line.text}</a>
                    ) : (
                      <p key={line.text} className="text-xs text-brand-muted">{line.text}</p>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-20">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                  style={{ background: 'linear-gradient(135deg, #F5EFE6, #C4915A)' }}
                >
                  <Icon name="PaperAirplaneIcon" size={36} className="text-primary" />
                </div>
                <h3 className="font-heading font-bold text-brand-fg text-2xl mb-3">消息已發送！</h3>
                <p className="text-brand-muted max-w-md leading-relaxed">
                  感謝您的來信！我們的客服團隊將在工作日 <strong className="text-primary">2小時內</strong> 回復您。
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 px-6 py-2.5 border border-brand-border rounded-full text-sm font-medium text-brand-muted hover:text-primary hover:border-primary/30 transition-colors"
                >
                  再次發送
                </button>
              </div>
            ) : (
              <div className="bg-secondary/30 rounded-3xl border border-brand-border p-8">
                <h2 className="font-heading font-bold text-brand-fg text-2xl mb-6">發送消息</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-fg mb-1.5">
                        姓名 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="您的姓名"
                        className="form-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-fg mb-1.5">公司名稱</label>
                      <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        placeholder="您的公司（選填）"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-fg mb-1.5">
                        電子郵件 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="example@company.com"
                        className="form-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-fg mb-1.5">聯繫電話</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+86 138 XXXX XXXX"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-fg mb-1.5">諮詢類型</label>
                    <select name="subject" value={form.subject} onChange={handleChange} className="form-input">
                      <option value="">請選擇諮詢類型</option>
                      <option value="wholesale">批發採購諮詢</option>
                      <option value="ecommerce">電商合作諮詢</option>
                      <option value="oem">OEM/ODM定制</option>
                      <option value="after-sales">售後服務</option>
                      <option value="other">其他</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-fg mb-1.5">
                      留言內容 <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="請詳細描述您的需求或問題..."
                      rows={5}
                      className="form-input resize-none"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-primary text-white rounded-full font-bold hover:bg-primary-dark transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {loading ? (
                      <>
                        <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
                        發送中...
                      </>
                    ) : (
                      <>
                        發送消息
                        <Icon name="PaperAirplaneIcon" size={18} />
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}